import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["content", "sidebar"]
  static values = { 
    guideId: Number,
    basePath: String
  }

  connect() {
    // Listen for turbo:frame-load events on the guide_content frame
    this.contentTarget.addEventListener('turbo:frame-load', this.handleFrameLoad.bind(this))
    
    // Listen for browser back/forward navigation
    window.addEventListener('popstate', this.handlePopState.bind(this))
  }

  disconnect() {
    this.contentTarget.removeEventListener('turbo:frame-load', this.handleFrameLoad.bind(this))
    window.removeEventListener('popstate', this.handlePopState.bind(this))
  }

  handleFrameLoad(event) {
    // Only handle events for the guide_content frame
    if (event.target.id !== 'guide_content') return
    
    // Get the current frame source URL
    const frameSource = event.target.src
    if (!frameSource) return

    // Parse the frame source to determine what URL to show
    let newPath = this.parsePathFromFrameSource(frameSource)

    // Update the browser URL without triggering a page reload
    if (newPath && window.location.pathname !== newPath) {
      window.history.pushState({}, '', newPath)
    }

    // Note: Sidebar updates are now handled by Rails turbo streams in the controller actions
    // No need to manually update the sidebar here
  }

  parsePathFromFrameSource(frameSource) {
    // Extract page ID if it's a page URL (either show_page or show with page_id)
    const pageMatch = frameSource.match(/\/guides\/(\d+)(?:\/pages\/(\d+)|.*[?&]page_id=(\d+))/)
    if (pageMatch) {
      const [, guideId, pageId1, pageId2] = pageMatch
      const pageId = pageId1 || pageId2
      if (pageId) {
        return `/guides/${guideId}/pages/${pageId}`
      }
    }
    
    // Extract quiz if it's a quiz URL  
    if (frameSource.includes('/quiz') || frameSource.includes('section=quiz')) {
      return `/guides/${this.guideIdValue}/quiz`
    }
    
    // Default to guide overview for the main guide page
    if (frameSource.includes(`/guides/${this.guideIdValue}`) && 
        !frameSource.includes('/pages/') && 
        !frameSource.includes('/quiz')) {
      return `/guides/${this.guideIdValue}`
    }

    return null
  }

  // Handle direct navigation clicks
  navigateToPage(event) {
    const link = event.currentTarget
    const href = link.getAttribute('href')
    
    if (href && window.location.pathname !== href) {
      // Update URL immediately for better UX
      window.history.pushState({}, '', href)
    }
  }

  navigateToQuiz(event) {
    const quizPath = `/guides/${this.guideIdValue}/quiz`
    if (window.location.pathname !== quizPath) {
      window.history.pushState({}, '', quizPath)
    }
  }

  startGuide(event) {
    // The start action will redirect to the first page, 
    // so we don't need to update the URL here as it will be handled by handleFrameLoad
  }

  handlePopState(event) {
    // Handle browser back/forward navigation
    const currentPath = window.location.pathname
    
    // Parse the current URL to determine what content to load
    const pageMatch = currentPath.match(/\/guides\/(\d+)\/pages\/(\d+)/)
    if (pageMatch) {
      // Load the specific page
      const [, guideId, pageId] = pageMatch
      const pageUrl = `/guides/${guideId}/pages/${pageId}`
      this.contentTarget.src = pageUrl
    } else if (currentPath.includes('/quiz')) {
      // Load the quiz
      const quizUrl = `/guides/${this.guideIdValue}/quiz`
      this.contentTarget.src = quizUrl
    } else if (currentPath === `/guides/${this.guideIdValue}`) {
      // Load the guide overview
      const overviewUrl = `/guides/${this.guideIdValue}`
      this.contentTarget.src = overviewUrl
    }
  }
} 